import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import AbsenceCalendar from '../../shared/absenceCalendar';
import PresetDropdown from '../../shared/presetDropdown';
import NotificationCard from '../../shared/notificationCard';
import AbsenceRequestDialog from '../../shared/absenceRequestDialog';
import Search from '../../shared/search';
import AlertDialog from '../../shared/alertDialog';
import CollapsableFilters from '../../shared/collapsableFilters';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  APP_PAGES,
  PARAMS,
  ABSENCE_CALENDAR_DEFAULT_PARAMS,
} from '../../../constants/pages';
import {
  api_absence_request_status,
  API_USERS_BASIC,
} from '../../../constants/apiRoutes';
import { ABSENCE_CALENDAR_INTERVAL_TYPES } from '../../../constants/absence';
import {
  USER_STATUSES,
  ABSENCE_REQUEST_STATUS_TYPES,
} from '../../../constants/statuses';
import { SYSTEM_ROLES, SYSTEM_ROLE_OPTIONS } from '../../../constants/roles';
import { PRESETS } from '../../../constants/presets';
import { sticky } from '../../../constants/helperCssRules';
import { PEOPLE_DEFAULT_ORDERING } from '../../../constants/people';
import {
  getAbsenceCalendar,
  getUserAbsenceCalendar,
  clearAbsenceCalendar,
} from '../../../store/modules/absenceCalendar';
import { absenceCalendarSelector } from '../../../store/selectors/absenceCalendarSelector';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import {
  addNewPreset,
  deleteSelectedPreset,
  editPresetActivity,
} from '../../../store/modules/organizationSettings';
import {
  prepareAbsenceCalendarInterval,
  prepareAbsenceCalendarDateRange,
  prepareAbsenceCalendarData,
} from '../../../utility/absence';
import { useTranslations } from '../../../utility/useTranslations';
import { useAvailableUsers, useCustomEffect } from '../../../utility/hooks';
import { prepareRoles } from '../../../utility/roles';
import { prepareAllSelectedFilters } from '../../shared/collapsableFilters/config';
import {
  hasNextPage,
  parseDuplicateParameters,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import {
  isArrayEmpty,
  getObjectToNumberArray,
  getItemById,
  isObjectEmpty,
  trimString,
  areArraysContentsEqual,
} from '../../../utility/helpers';
import { preparePeopleFilters } from '../../../utility/people';
import { formatDate, checkIsSameDay } from '../../../utility/dateUtils';
import http from '../../../utility/http';
import {
  getActivePresetId,
  getActivePresetName,
  getFilteredPresets,
  getPlaceholderName,
  handleHasMaxPresets,
  handlePresetName,
} from '../../../utility/presets';
import {
  isUserProfileAccessibleCheck,
  hasScopedAuthPermissionCheck,
} from '../../../utility/permissions';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    stickyHeader: {
      padding: spacing(8, 0, 1, 0),
      ...sticky(primary.white, 105),
    },
    selectors: {
      borderBottom: `1px solid ${primary.bluish7}`,
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: spacing(2),
    },
    periodSelection: {
      display: 'grid',
      gridTemplateColumns: '220px 1fr',
      gridColumnGap: 8,
      paddingTop: spacing(2),
      paddingBottom: spacing(5.5),
      [breakpoints.up('xl')]: {
        gridTemplateColumns: '298px 1fr',
      },
    },
    period: {
      display: 'flex',
      gap: 16,
    },
    search: {
      width: 300,
      display: 'flex',
      alignItems: 'end',
      maxHeight: 66,
    },
    interval: {
      minWidth: 110,
    },
    todayButton: {
      alignSelf: 'end',
    },
    arrowButtonWrapper: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridColumnGap: 4,
    },
    arrowButton: {
      height: 40,
      width: 40,
      alignSelf: 'end',
    },
    arrowButtonRight: {
      transform: 'rotate(180deg)',
    },
    arrowButtonIcon: {
      height: 16,
      width: 12,
    },
    noResults: {
      marginTop: spacing(3),
      maxWidth: 900,
    },
    collapsableFilter: {
      paddingTop: spacing(6.5),
    },
    presets: {
      width: 350,
    },
    presetPaper: {
      zIndex: 105,
    },
    label: {
      paddingTop: spacing(2),
      alignItems: 'center',
      display: 'flex',
    },
    intervalLabel: {
      lineHeight: '20px',
    },
  })
);

const {
  ID,
  ROLE,
  JOB_TITLE,
  TRACK,
  LEVEL,
  DIVISION,
  DEPARTMENT,
  TAGS,
  SEARCH,
  EXCLUDE_ROLE,
  EXCLUDE_STATUS,
  PAGE,
  CALENDAR_FROM,
  CALENDAR_TO,
} = PARAMS;

const ABSENCE_CALENDAR_PRESET_ID = PRESETS.ABSENCE_CALENDAR;
const PRESET_DROPDOWN_WIDTH = '350px';
const PRESET_INPUT_WIDTH = 350;
const PRESET_FILTER_KEYS = [
  ID,
  ROLE,
  JOB_TITLE,
  TRACK,
  LEVEL,
  DIVISION,
  DEPARTMENT,
  TAGS,
];

const DEFAULT_FILTERS = {
  [ID]: [],
  [ROLE]: [],
  [JOB_TITLE]: [],
  [TRACK]: [],
  [LEVEL]: [],
  [DIVISION]: [],
  [DEPARTMENT]: [],
  [TAGS]: [],
};

const AbsenceCalendarPage = ({
  location,
  auth,
  accessControl,
  organizationSettings,
  presets,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.ABSENCE_CALENDAR);
  const { page: defaultPage } = ABSENCE_CALENDAR_DEFAULT_PARAMS;

  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { categories } = useSelector(tagsSelector);
  const absenceCalendar = useSelector(absenceCalendarSelector);

  const [search, setSearch] = useState('');
  const [noResultsMessage, setNoResultsMessage] = useState(
    translations.noResults
  );
  const [selectedFilters, setSelectedFilters] = useState(DEFAULT_FILTERS);

  const canResolveRequestCheck = useCallback(
    user => {
      const hasResolvePermission = hasScopedAuthPermissionCheck(
        PERMISSION_CODES.ABSENCE_REQUESTS_MANAGE,
        accessControl,
        auth.id,
        user.id
      );
      if (organizationSettings.absence_double_approval) {
        return auth.id === user.report_to || hasResolvePermission;
      }

      return hasResolvePermission;
    },
    [auth, accessControl, organizationSettings]
  );

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [interval, setInterval] = useState(
    ABSENCE_CALENDAR_INTERVAL_TYPES.WEEK
  );
  const [intervalPage, setIntervalPage] = useState(0);
  const [calendar, setCalendar] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [isAbsenceRequestDialogOpened, setIsAbsenceRequestDialogOpened] =
    useState(false);
  const hasResults =
    !isObjectEmpty(calendar) &&
    !isObjectEmpty(absenceCalendar) &&
    !isArrayEmpty(absenceCalendar.results);
  const [selectedPreset, setSelectedPreset] = useState(undefined);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(false);
  const [presetPlaceholderName, setPresetPlaceholderName] = useState('');
  const [alreadySavedMessage, setAlreadySavedMessage] = useState('');
  const [isDeletePresetDialogOpened, setIsDeletePresetDialogOpened] =
    useState(false);

  const allUsersParams = useMemo(
    () => ({
      [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
      [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
    }),
    []
  );
  const allUsers = useAvailableUsers(allUsersParams, null);
  const intervalOptions = prepareAbsenceCalendarInterval(translations.interval);
  const activePresetId = getActivePresetId(presets, ABSENCE_CALENDAR_PRESET_ID);
  const filteredPresets = getFilteredPresets(
    presets,
    ABSENCE_CALENDAR_PRESET_ID
  );
  const presetName = getActivePresetName(filteredPresets, activePresetId);
  const handledPresetName = handlePresetName(
    filteredPresets,
    activePresetId,
    presetName,
    {
      defaultPlaceholder,
      presetPlaceholderName,
      isLoading: isInitialLoad,
    },
    translations.presets.startSearch
  );
  const placeholderName = getPlaceholderName(
    handledPresetName,
    presetName,
    translations.presets.startSearch
  );
  const initialPreset = getItemById(filteredPresets, activePresetId);
  const hasLoadMore = hasNextPage(absenceCalendar);
  const absenceRequests = useMemo(() => {
    if (absenceCalendar.results && !isArrayEmpty(absenceCalendar.results)) {
      const userReport = absenceCalendar.results.find(
        report => report.user.id === selectedUser?.id
      );

      return (
        userReport?.intervalDays?.find(day =>
          checkIsSameDay(selectedDay, day.date)
        )?.absenceRequests || []
      );
    }
    return [];
  }, [absenceCalendar, selectedUser, selectedDay]);

  const peopleFilters = useMemo(
    () =>
      preparePeopleFilters(
        translations,
        jobTitles,
        tracks,
        divisions,
        departments,
        prepareRoles(translations.systemRoles, SYSTEM_ROLE_OPTIONS, true),
        allUsers,
        categories
      ),
    [
      translations,
      jobTitles,
      tracks,
      divisions,
      departments,
      categories,
      allUsers,
    ]
  );

  const handleGetAbsenceCalendarData = ({
    newPage,
    newUsers,
    newInterval,
    newIntervalPage,
    isLoadMore,
  } = {}) => {
    const dateRange = prepareAbsenceCalendarDateRange(
      newInterval,
      newIntervalPage
    );
    const newCalendarData = prepareAbsenceCalendarData(dateRange, newInterval);
    setCalendar(newCalendarData);

    const params = {
      [PAGE]: newPage,
      [ID]: getObjectToNumberArray(newUsers),
      [CALENDAR_FROM]: formatDate(dateRange.startDate, 'yyyy-MM-dd'),
      [CALENDAR_TO]: formatDate(dateRange.endDate, 'yyyy-MM-dd'),
    };

    if (isArrayEmpty(newUsers)) {
      setCalendar({});
      return dispatch(clearAbsenceCalendar());
    }

    return getAbsenceCalendar(dispatch, isLoadMore, newCalendarData, params);
  };

  const handleGetUserCalendarData = currentUserId => {
    const dateRange = prepareAbsenceCalendarDateRange(interval, intervalPage);
    const params = {
      [PAGE]: defaultPage,
      [ID]: [currentUserId],
      [CALENDAR_FROM]: formatDate(dateRange.startDate, 'yyyy-MM-dd'),
      [CALENDAR_TO]: formatDate(dateRange.endDate, 'yyyy-MM-dd'),
    };

    return getUserAbsenceCalendar(dispatch, calendar, params);
  };

  const onGoToUserProfile = userId => () =>
    navigate(`/people/${userId}/absences`);

  const canAccessUserProfileCheck = useCallback(
    userId => isUserProfileAccessibleCheck(accessControl, auth.id, userId),
    [auth, accessControl]
  );

  const handleResetPreset = () => {
    editPresetActivity(dispatch, -1, ABSENCE_CALENDAR_PRESET_ID, false);
    setSelectedPreset(undefined);
    setAlreadySavedMessage('');
  };

  const getPresetFiltersData = filters =>
    PRESET_FILTER_KEYS.reduce(
      (acc, key) => ({
        ...acc,
        ...(!isArrayEmpty(filters[key]) ? { [key]: filters[key] } : {}),
      }),
      {}
    );

  const arePresetFiltersEqual = (currentFilters, presetFilters) =>
    PRESET_FILTER_KEYS.every(key =>
      areArraysContentsEqual(
        currentFilters[key] || [],
        presetFilters?.[key] || []
      )
    );

  const checkValueInPresets = currentFilters => {
    const absenceCalendarPresets = presets[ABSENCE_CALENDAR_PRESET_ID];

    if (absenceCalendarPresets && !isArrayEmpty(absenceCalendarPresets)) {
      const activePreset = absenceCalendarPresets.find(preset => {
        const presetData = preset?.data;

        return arePresetFiltersEqual(currentFilters, presetData?.filters || {});
      });

      if (activePreset) {
        editPresetActivity(
          dispatch,
          activePreset.id,
          ABSENCE_CALENDAR_PRESET_ID,
          true
        );
        setSelectedPreset(activePreset);
        setAlreadySavedMessage(translations.presets.used);
      } else {
        handleResetPreset();
      }
    }
  };

  const handleSelectUsers = users => {
    setPage(defaultPage);
    setSelectedUsers(users);
    handleGetAbsenceCalendarData({
      newPage: defaultPage,
      newUsers: users,
      newInterval: interval,
      newIntervalPage: intervalPage,
    });
  };

  const onSearch = async (text = '') => {
    const searchTerm = trimString(text);
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...selectedFilters,
        ...PEOPLE_DEFAULT_ORDERING,
        ...(searchTerm ? { [SEARCH]: searchTerm } : {}),
        [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });

    if (isArrayEmpty(data)) {
      setNoResultsMessage(translations.noCriteriaResults);
    }
    setSearch(text);
    handleSelectUsers(data);
  };

  const handleChangeInterval = value => {
    if (value !== interval) {
      setInterval(value);
      setIntervalPage(0);
      setPage(defaultPage);
      handleGetAbsenceCalendarData({
        newPage: defaultPage,
        newUsers: selectedUsers,
        newInterval: value,
        newIntervalPage: 0,
      });
    }
  };

  const handlePlaceholder = name => {
    setDefaultPlaceholder(false);
    setPresetPlaceholderName(name);
  };

  const handleDeletePresetDialog = preset => {
    setSelectedPreset(preset);
    setIsDeletePresetDialogOpened(true);
  };

  const handleDeletePreset = () => {
    deleteSelectedPreset(
      dispatch,
      selectedPreset.id,
      ABSENCE_CALENDAR_PRESET_ID
    ).then(() => {
      setIsDeletePresetDialogOpened(false);
      if (activePresetId === selectedPreset.id) {
        setSelectedPreset(undefined);
        setAlreadySavedMessage('');
      }
      showSuccessMessage(translations.presets.successMessages.deletedPreset);
    });
  };

  const handleSavePreset = name => {
    const data = {
      filters: getPresetFiltersData(selectedFilters),
    };

    addNewPreset(dispatch, {
      name,
      location: ABSENCE_CALENDAR_PRESET_ID,
      data,
    }).then(() => {
      setAlreadySavedMessage(translations.presets.used);
      showSuccessMessage(translations.presets.successMessages.createdPreset);
    });
  };

  const handleFilterResults = async (
    selected,
    shouldSkipPresetCheck = false,
    shouldUseSearch = true
  ) => {
    const uSearch = shouldUseSearch ? search : '';
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...selected,
        ...PEOPLE_DEFAULT_ORDERING,
        ...(uSearch ? { [SEARCH]: uSearch } : {}),
        [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });
    setSelectedFilters(selected);
    if (!shouldSkipPresetCheck) {
      checkValueInPresets(selected);
    }
    handleSelectUsers(data);
  };

  const handleApplyPreset = preset => {
    const presetData = preset?.data;
    const presetFilters = {
      ...DEFAULT_FILTERS,
      ...(presetData?.filters || {}),
    };

    editPresetActivity(dispatch, preset.id, ABSENCE_CALENDAR_PRESET_ID, true);
    setSelectedPreset(preset);
    setSelectedFilters(presetFilters);
    setSearch('');
    handleFilterResults(presetFilters, true, false);
  };

  const handleSetCurrentDate = () => {
    setIntervalPage(0);
    setPage(defaultPage);
    handleGetAbsenceCalendarData({
      newPage: defaultPage,
      newUsers: selectedUsers,
      newInterval: interval,
      newIntervalPage: 0,
    });
  };

  const handleNextPeriod = () => {
    setIntervalPage(prevPage => prevPage + 1);
    setPage(defaultPage);
    handleGetAbsenceCalendarData({
      newPage: defaultPage,
      newUsers: selectedUsers,
      newInterval: interval,
      newIntervalPage: intervalPage + 1,
    });
  };

  const handlePreviousPeriod = () => {
    setIntervalPage(prevPage => prevPage - 1);
    setPage(defaultPage);
    handleGetAbsenceCalendarData({
      newPage: defaultPage,
      newUsers: selectedUsers,
      newInterval: interval,
      newIntervalPage: intervalPage - 1,
    });
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handleGetAbsenceCalendarData({
      newPage: page + 1,
      newUsers: selectedUsers,
      newInterval: interval,
      newIntervalPage: intervalPage,
      isLoadMore: true,
    });
  };

  const onOpenRequestDialog = (user, currentDay) => {
    setSelectedUser(user);
    setSelectedDay(currentDay);
    setIsAbsenceRequestDialogOpened(true);
  };

  const onCloseRequestDialog = () => {
    setSelectedUser(null);
    setSelectedDay('');
    setIsAbsenceRequestDialogOpened(false);
  };

  const onApproveRequest = async request => {
    await http.patch(api_absence_request_status(request.id), {
      [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.APPROVED,
    });
    handleGetUserCalendarData(request.user.id);
  };

  const onRejectRequest = async request => {
    await http.patch(api_absence_request_status(request.id), {
      [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.REJECTED,
    });
    handleGetUserCalendarData(request.user.id);
  };

  const getInitialCalendarData = async () => {
    try {
      if (!isArrayEmpty(allUsers)) {
        await Promise.all([
          getJobTitles(dispatch),
          getTagsCategories(dispatch),
          getLevelsAndTracks(dispatch),
          getDivisionsAndDepartments(dispatch),
        ]);
        const presetFilters = initialPreset?.data?.filters
          ? {
              ...DEFAULT_FILTERS,
              ...initialPreset?.data?.filters,
            }
          : selectedFilters;
        const presetUsers = initialPreset
          ? (
              await http.get(API_USERS_BASIC, {
                params: {
                  ...presetFilters,
                  ...PEOPLE_DEFAULT_ORDERING,
                  [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
                  [EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
                },
                paramsSerializer: d => parseDuplicateParameters(d),
              })
            ).data
          : allUsers;
        await handleGetAbsenceCalendarData({
          newPage: page,
          newUsers: presetUsers,
          newInterval: interval,
          newIntervalPage: intervalPage,
        });
        setSelectedUsers(presetUsers);
        setSelectedFilters(presetFilters);
        if (initialPreset) {
          setAlreadySavedMessage(translations.presets.used);
          setSelectedPreset(initialPreset);
        }
        setIsInitialLoad(false);
      }
    } catch {
      setIsInitialLoad(false);
    }
  };

  const cleanup = useCallback(() => {
    dispatch(clearAbsenceCalendar());
    dispatch(clearJobTitles());
    dispatch(clearTagsCategories());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  useCustomEffect(getInitialCalendarData, [allUsers], false);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const presetSummary = useMemo(() => {
    const presetFilters = getPresetFiltersData(selectedFilters);
    const filterSections = prepareAllSelectedFilters(peopleFilters, {
      ...DEFAULT_FILTERS,
      ...presetFilters,
    }).map(filter => ({
      title: filter.name,
      isUserList: filter.id === ID,
      users: filter.id === ID ? filter.selectedItems : [],
      items: filter.id === ID ? [] : filter.selectedItems,
    }));

    return {
      hasFilterSections: true,
      translations: {},
      filterSections,
    };
  }, [peopleFilters, selectedFilters]);

  return (
    <PageContainer
      {...rest}
      translations={translations}
      location={location}
      auth={auth}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      dispatch={dispatch}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <div>
              <Typography variant="body2">
                {translations.description}
              </Typography>
            </div>
          </div>
          <div className={classes.selectors}>
            <CollapsableFilters
              className={classes.collapsableFilter}
              selectedFilters={selectedFilters}
              translations={translations.filters}
              filters={peopleFilters}
              onApplyFilters={handleFilterResults}
              shouldSortAlphabetically
              shouldRemoveFieldSpacing
              shouldRemoveFieldError
              shouldCalculateSelectionHeight
            />
            <div className={classes.period}>
              <Search
                className={classes.search}
                placeholder={translations.searchByName}
                value={search}
                onChange={onSearch}
              />
              <PresetDropdown
                className={classes.presets}
                paperClass={classes.presetPaper}
                translations={translations.presets}
                dropdownWidth={PRESET_DROPDOWN_WIDTH}
                inputWidth={PRESET_INPUT_WIDTH}
                presetPlaceholderName={placeholderName}
                options={filteredPresets}
                activePreset={activePresetId}
                handleApply={handleApplyPreset}
                handleDelete={handleDeletePresetDialog}
                handleSavePreset={handleSavePreset}
                disabledPreset={
                  handleHasMaxPresets(filteredPresets, translations.presets) ||
                  (isArrayEmpty(selectedUsers)
                    ? translations.presets.oneOnOnePresetConditionTooltip
                    : '')
                }
                alreadySavedMessage={alreadySavedMessage}
                presetSummary={presetSummary}
                handlePlaceholder={handlePlaceholder}
              />
            </div>
          </div>
          {hasResults && (
            <AbsenceCalendar
              translations={translations.absenceCalendar}
              absenceReports={absenceCalendar.results}
              calendar={calendar}
              selectedInterval={interval}
              intervalOptions={intervalOptions}
              selectedUsersCount={selectedUsers?.length}
              hasDoubleApprove={organizationSettings.absence_double_approval}
              hasLoadMore={hasLoadMore}
              onLoadMore={onLoadMore}
              canResolveRequestCheck={canResolveRequestCheck}
              canAccessUserProfileCheck={canAccessUserProfileCheck}
              onGoToUserProfile={onGoToUserProfile}
              onResolveRequest={onOpenRequestDialog}
              onChangeInterval={handleChangeInterval}
              onSetCurrentdate={handleSetCurrentDate}
              onNextPeriod={handleNextPeriod}
              onPreviousPeriod={handlePreviousPeriod}
            />
          )}
          <NotificationCard
            className={classes.noResults}
            content={noResultsMessage}
            shouldFade={!hasResults}
          />
          <AbsenceRequestDialog
            translations={translations.absenceRequestDialog}
            isOpened={isAbsenceRequestDialogOpened}
            user={selectedUser}
            auth={auth}
            absenceRequests={absenceRequests}
            isDoubleApprove={organizationSettings.absence_double_approval}
            onApproveRequest={onApproveRequest}
            onRejectRequest={onRejectRequest}
            onClose={onCloseRequestDialog}
          />
          <AlertDialog
            translations={translations.presets.presetDeleteDialog}
            isOpened={isDeletePresetDialogOpened}
            onClose={() => setIsDeletePresetDialogOpened(false)}
            onConfirm={handleDeletePreset}
            isWarning
          />
        </div>
      )}
    </PageContainer>
  );
};

AbsenceCalendarPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  presets: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(AbsenceCalendarPage);
